/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import { ShareAccordion } from '@cedalo/webui-extensions';
import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup, MenuItem, TextField } from '@mui/material';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import JSG from '@cedalo/jsg-ui';
import * as Actions from '../../actions/actions';
import { FormattedMessage } from 'react-intl';
import DialogButton from '../utils/DialogButton';
import SettingsPaper from '../utils/SettingsPaper';
import { StreamSheetAccordion } from '../utils/StreamSheetAccordion';
import { graphManager } from '../../GraphManager';
import { withAppData } from '../app/AppProvider';

function ShareSettingsComponent(props) {
	const [expanded, setExpanded] = useState('panelViewSettings');

	const handleClose = () => {
		props.close();
		props.showViewMode(false);
	};

	const escape = (event) => {
		if (event.key === 'Escape') {
			handleClose();
		}
	};

	useEffect(() => {
		window.addEventListener('keyup', escape);
		// Remove event listeners on cleanup
		return () => {
			window.removeEventListener('keyup', escape);
		};
	}, []);

	const updateSettings = (machineId, settings) => {
		props.updateMachineSettings(machineId, settings);
		props.updateShareSettings(settings);
	};

	const saveViewSettings = (viewSettings) => {
		const { active, ...view } = viewSettings;
		updateSettings(props.machineId, { view });
	};

	const handleOutboxChange = (event, state) => {
		const viewSettings = { ...props.viewSettings, showOutbox: state };
		saveViewSettings(viewSettings);
	};

	const handleHeaderChange = (event, state) => {
		const viewSettings = { ...props.viewSettings, showHeader: state };
		saveViewSettings(viewSettings);
	};

	const handleGridChange = (event, state) => {
		const viewSettings = { ...props.viewSettings, showGrid: state };
		saveViewSettings(viewSettings);
	};

	const handleInboxChange = (event, state) => {
		const viewSettings = { ...props.viewSettings, showInbox: state };
		saveViewSettings(viewSettings);
	};

	const handleScrollChange = (event, state) => {
		const viewSettings = { ...props.viewSettings, allowScroll: state };
		saveViewSettings(viewSettings);
	};

	const handleZoomChange = (event, state) => {
		const viewSettings = { ...props.viewSettings, allowZoom: state };
		saveViewSettings(viewSettings);
	};

	const handleMaximizeChange = (event) => {
		const viewSettings = { ...props.viewSettings, maximize: event.target.value };
		saveViewSettings(viewSettings);
	};

	const handleChange = (exp) => {
		setExpanded(exp === expanded ? undefined : exp);
	};

	const handleOpenPreview = () => {
		window.open(`${window.location.origin}/apps/${props.machineId}#preview=true`, '_blank');
	};

	if (!props.open) {
		return null;
	}

	const container = graphManager.machineContainer;
	if (!container) {
		return null;
	}

	const sheetNames = JSG.streamSheets.map((streamSheetModel) => {
		return {
			name: streamSheetModel.properties.name,
			id: streamSheetModel.properties.sheetId
		};
	});

	const getStreamSheetType = (id) => {
		const model = JSG.streamSheets.find((streamSheet) => streamSheet.id === id);
		return model ? model.properties.sheetType : '';
	};

	const { viewSettings } = props;
	const sheetType = getStreamSheetType(viewSettings.maximize);

	return (
		<SettingsPaper
			open={props.open}
			close={() => handleClose()}
			helpContext="guides/app/shareapp"
			title={<FormattedMessage id="ShareSettings.Title" defaultMessage="Share Settings" />}
		>
			<DialogButton sx={{ width: '100%' }} marginV onClick={() => handleOpenPreview()}>
				<FormattedMessage id="ShareSettings.OpenInNewTab" defaultMessage="Open in new Tab" />
			</DialogButton>
			<DialogButton sx={{ width: '100%' }} marginV onClick={() => handleClose()}>
				<FormattedMessage id="ShareSettings.HideViewSettings" defaultMessage="Close Presentation Mode" />
			</DialogButton>
			<div style={{ height: '15px' }} />
			{props.appEditable ?
				<StreamSheetAccordion
					id="panelViewSettings"
					expanded={expanded === 'panelViewSettings'}
					onChange={() => handleChange('panelViewSettings')}
					title={<FormattedMessage id="ShareSettings.PresentationMode" defaultMessage="Presentation Mode"/>}
					details={<FormattedMessage id="ShareSettings.PresentationModeDetails" defaultMessage="Details"/>}
				>
					<FormGroup>
						<TextField
							variant="outlined"
							size="small"
							margin="normal"
							sx={{mb: '10px'}}
							label={<FormattedMessage id="ShareSettings.DisplaySheet" defaultMessage="Display Sheet"/>}
							select
							fullWidth
							id="templates"
							value={viewSettings.maximize}
							onChange={(event) => handleMaximizeChange(event)}
						>
							{sheetNames.map((key) => (
								<MenuItem value={key.id} key={key.id}>
									{key.name}
								</MenuItem>
							))}
						</TextField>
						{sheetType === 'sheet'
							? [
								<FormControlLabel
									key="header"
									control={
										<Checkbox
											checked={viewSettings.showHeader}
											onChange={(event, state) => handleHeaderChange(event, state)}
										/>
									}
									label={<FormattedMessage id="ShareSettings.Header" defaultMessage="Show Header"/>}
								/>,
								<FormControlLabel
									key="grid"
									control={
										<Checkbox
											checked={viewSettings.showGrid}
											onChange={(event, state) => handleGridChange(event, state)}
										/>
									}
									label={<FormattedMessage id="ShareSettings.Grid" defaultMessage="Show Grid"/>}
								/>
							]
							: null}
						<FormControlLabel
							key="inbox"
							control={
								<Checkbox
									checked={viewSettings.showInbox}
									onChange={(event, state) => handleInboxChange(event, state)}
								/>
							}
							label={<FormattedMessage id="ShareSettings.Inbox" defaultMessage="Show Inbox"/>}
						/>
						<FormControlLabel
							key="outbox"
							control={
								<Checkbox
									checked={viewSettings.showOutbox}
									onChange={(event, state) => handleOutboxChange(event, state)}
								/>
							}
							label={<FormattedMessage id="ShareSettings.ShowOutbox" defaultMessage="Show Outbox"/>}
						/>
						<FormControlLabel
							key="scroll"
							control={
								<Checkbox
									checked={viewSettings.allowScroll}
									onChange={(event, state) => handleScrollChange(event, state)}
								/>
							}
							label={<FormattedMessage id="ShareSettings.Scrollbars" defaultMessage="Allow Scrolling"/>}
						/>
						<FormControlLabel
							key="zoom"
							control={
								<Checkbox
									checked={viewSettings.allowZoom}
									onChange={(event, state) => handleZoomChange(event, state)}
								/>
							}
							label={<FormattedMessage id="ShareSettings.Zoom" defaultMessage="Allow Zooming"/>}
						/>
					</FormGroup>
				</StreamSheetAccordion>
			: null}
			<ShareAccordion
				sx={{ borderBottom: '1px solid red' }}
				expanded={expanded}
				appEditable={props.appEditable}
				appId={props.machineId}
				handleExpanded={(name) => handleChange(name)}
			/>
		</SettingsPaper>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export const ShareSettings = withAppData(({ machine, dispatchAppData }) => ({
	machineId: machine.id,
	settings: machine.settings,
	viewSettings: machine.settings.view,
	updateShareSettings: (settings) => dispatchAppData({ type: 'update-machine-settings', data: settings })
}))(connect(null, mapDispatchToProps)(ShareSettingsComponent));
